import type { CSSProperties } from "react";
import { useEffect, useState } from "react";

import { fetchCommercialState } from "../../lib/admin/commercial";
import {
  contractCountdown,
  formatMrr,
  type CommercialState,
} from "../../lib/admin/commercialState.mjs";

/**
 * MER-212 / MER-486: read-only commercial state card on the admin
 * customer detail page. Source of truth is the staff-only
 * tenant_commercial table (migration 0039). The original
 * bundled-YAML source was retired before any values shipped: the
 * bundle is publicly fetchable, so MRR / contacts / contract terms
 * could not live there (AJ decision on MER-185).
 */

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(`${iso}T00:00:00Z`);
  if (!Number.isFinite(t)) return null;
  return new Date(t).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

type PanelState =
  | { phase: "loading" }
  | { phase: "ready"; commercial: CommercialState | null }
  | { phase: "error"; message: string };

export default function CommercialPanel(props: { tenantId: string }) {
  const { tenantId } = props;
  const [state, setState] = useState<PanelState>({ phase: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ phase: "loading" });
    fetchCommercialState(tenantId)
      .then((commercial) => {
        if (!cancelled) setState({ phase: "ready", commercial });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ phase: "error", message: err instanceof Error ? err.message : String(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (state.phase === "loading") return null;

  if (state.phase === "error") {
    return (
      <section style={panelStyle}>
        <h2 style={panelTitleStyle}>Commercial</h2>
        <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--flag-red)" }}>
          Could not load commercial state: {state.message}
        </div>
      </section>
    );
  }

  return (
    <section style={panelStyle}>
      <h2 style={panelTitleStyle}>Commercial</h2>
      {state.commercial === null ? (
        <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>
          Commercial state not configured. Add this customer's row to the staff-only{" "}
          <code>tenant_commercial</code> table (Supabase SQL editor; template in migration 0039).
        </div>
      ) : (
        <CommercialBody commercial={state.commercial} />
      )}
    </section>
  );
}

function CommercialBody(props: { commercial: CommercialState }) {
  const { commercial } = props;
  const contact = commercial.partnerContact;
  const mrr = formatMrr(commercial.mrrUsd);
  const start = formatDate(commercial.contractStart);
  const end = formatDate(commercial.contractEnd);
  const countdown = contractCountdown(commercial.contractEnd);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
      <div>
        <div style={blockLabelStyle}>Partner contact</div>
        {contact ? (
          <div style={{ display: "grid", gap: 2, fontSize: 13 }}>
            {contact.name ? (
              <span style={{ fontWeight: 600, color: "var(--text)" }}>{contact.name}</span>
            ) : null}
            {contact.role ? <span style={{ color: "var(--text-2)" }}>{contact.role}</span> : null}
            {contact.email ? (
              <a href={`mailto:${contact.email}`} style={{ color: "var(--brand-deep)" }}>
                {contact.email}
              </a>
            ) : null}
          </div>
        ) : (
          <div style={notSetStyle}>Not set</div>
        )}
      </div>
      <div>
        <div style={blockLabelStyle}>Commercial</div>
        <div style={{ display: "grid", gap: 2, fontSize: 13, color: "var(--text)" }}>
          <span style={{ fontWeight: 700 }}>{mrr ?? "MRR not set"}</span>
          {start ? <span style={{ color: "var(--text-2)" }}>Starts {start}</span> : null}
          {end ? (
            <span style={{ color: "var(--text-2)" }}>
              Ends {end}
              {countdown ? ` (${countdown})` : ""}
            </span>
          ) : null}
        </div>
      </div>
      <div>
        <div style={blockLabelStyle}>Notes</div>
        {commercial.notes ? (
          <div style={{ fontSize: 13, color: "var(--text)", whiteSpace: "pre-wrap" }}>
            {commercial.notes}
          </div>
        ) : (
          <div style={notSetStyle}>No notes</div>
        )}
      </div>
    </div>
  );
}

const panelStyle: CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 12,
  padding: 18,
  display: "grid",
  gap: 12,
};

const panelTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 14,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--text-2)",
};

const blockLabelStyle: CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  fontWeight: 700,
  marginBottom: 4,
};

const notSetStyle: CSSProperties = {
  fontSize: 13,
  color: "var(--text-muted)",
};
